import { useState } from 'react';
import { Shell } from '../Shell';
import { useNav } from '../nav';
import { useAsync } from '../../core/useAsync';
import { ApiError, createPreset, deletePreset, getPresets, updatePreset, type PresetInput } from '../../core/api';
import { summarise, type Preset } from '../presets';
import s from './Presets.module.css';

// Admin: named scoring presets members pick when proposing a round. List + an inline
// editor, wired to GET/POST/PUT/DELETE /api/mgmt/presets. Delete 409s if a round
// references the preset (surfaced as an error).

const blank: Preset = { ruleset_id: 0, name: '', budget: 20, track_min: 0, track_max: 20, negative_multiplier: 2, veto_threshold: 5, veto_threshold_pct: 50, veto_penalty: 10, popularity_bonus: 3, popularity_tie_bonus: 1, veto_enabled: true };

// The numeric fields are edited as STRINGS so you can clear a value or type a
// negative (e.g. a negative Min/track for down-votes) instead of the number input
// clamping "0" in front of what you type. Parsed to ints on save. Veto threshold is a
// PERCENTAGE of players (1–100) — the backend derives the live count from it.
const NUMS = ['budget', 'track_max', 'track_min', 'veto_threshold_pct', 'veto_penalty'] as const;
type NumKey = (typeof NUMS)[number];
const NUM_LABEL: Record<NumKey, string> = { budget: 'Budget', track_max: 'Max / track', track_min: 'Min / track', veto_threshold_pct: 'Veto threshold %', veto_penalty: 'Veto penalty' };
const emptyNform = (): Record<NumKey, string> => ({ budget: '', track_max: '', track_min: '', veto_threshold_pct: '', veto_penalty: '' });

const payloadOf = (p: Preset): PresetInput => ({
  name: p.name.trim(), budget: p.budget, track_min: p.track_min, track_max: p.track_max,
  negative_multiplier: p.negative_multiplier, veto_threshold_pct: p.veto_threshold_pct ?? 50, veto_penalty: p.veto_penalty,
  popularity_bonus: p.popularity_bonus, popularity_tie_bonus: p.popularity_tie_bonus, veto_enabled: p.veto_enabled,
});

export function Presets() {
  const nav = useNav();
  const list = useAsync<Preset[]>(getPresets, []);
  const [editing, setEditing] = useState<Preset | null>(null);
  const [nform, setNform] = useState<Record<NumKey, string>>(emptyNform);
  const [busy, setBusy] = useState<number | 'save' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function openEditor(p: Preset) {
    setErr(null);
    setEditing(p);
    setNform({ budget: String(p.budget), track_max: String(p.track_max), track_min: String(p.track_min), veto_threshold_pct: String(p.veto_threshold_pct ?? 50), veto_penalty: String(p.veto_penalty) });
  }

  // Parse the string fields to ints; NaN marks an invalid/empty one.
  const parseNums = (): Record<NumKey, number> => {
    const out = {} as Record<NumKey, number>;
    for (const k of NUMS) {
      const raw = (nform[k] ?? '').trim();
      const n = Number(raw);
      out[k] = raw === '' || !Number.isInteger(n) ? NaN : n;
    }
    return out;
  };

  async function save() {
    if (!editing || !editing.name.trim() || busy != null) return;
    const nums = parseNums();
    const bad = NUMS.find((k) => Number.isNaN(nums[k]));
    if (bad) { setErr(`Enter a whole number for ${NUM_LABEL[bad]}.`); return; }
    if (nums.track_min > nums.track_max) { setErr('Min / track can’t be more than Max / track.'); return; }
    if (nums.veto_threshold_pct < 1 || nums.veto_threshold_pct > 100) { setErr('Veto threshold % must be between 1 and 100.'); return; }
    setBusy('save'); setErr(null);
    try {
      const payload = payloadOf({ ...editing, ...nums });
      if (editing.ruleset_id) await updatePreset(editing.ruleset_id, payload);
      else await createPreset(payload);
      setEditing(null);
      list.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save the preset.');
    } finally { setBusy(null); }
  }

  async function del(id: number) {
    if (busy != null || !window.confirm('Delete this preset?')) return;
    setBusy(id); setErr(null);
    try { await deletePreset(id); list.reload(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not delete the preset.'); }
    finally { setBusy(null); }
  }

  if (editing) {
    const set = (patch: Partial<Preset>) => setEditing({ ...editing, ...patch });
    const setN = (k: NumKey) => (e: React.ChangeEvent<HTMLInputElement>) => setNform((f) => ({ ...f, [k]: e.target.value }));
    // Preview summary from the current strings (NaN → 0 just for the preview text).
    const nums = parseNums();
    const preview: Preset = { ...editing, budget: nums.budget || 0, track_max: nums.track_max || 0, track_min: Number.isNaN(nums.track_min) ? 0 : nums.track_min, veto_threshold_pct: nums.veto_threshold_pct || 50 };
    return (
      <Shell>
        <div className={s.appbar}><span className={s.ctx}>{editing.ruleset_id ? 'Edit preset' : 'New preset'}</span></div>
        {err ? <div className={s.banner}>{err}</div> : null}
        <div className={s.ed}>
          <div className={s.field}>
            <label>Name</label>
            <input className={s.tin} value={editing.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Standard" maxLength={40} />
          </div>
          <div className={s.grid2}>
            <div className={s.field}><label>Budget</label><input className={s.tin} type="text" inputMode="numeric" value={nform.budget} onChange={setN('budget')} /></div>
            <div className={s.field}><label>Max / track</label><input className={s.tin} type="text" inputMode="numeric" value={nform.track_max} onChange={setN('track_max')} /></div>
            <div className={s.field}><label>Min / track</label><input className={s.tin} type="text" inputMode="text" placeholder="0 or negative" value={nform.track_min} onChange={setN('track_min')} /></div>
            <div className={s.field}><label>Veto threshold %</label><input className={s.tin} type="text" inputMode="numeric" placeholder="50" value={nform.veto_threshold_pct} onChange={setN('veto_threshold_pct')} /></div>
            <div className={s.field}><label>Veto penalty</label><input className={s.tin} type="text" inputMode="numeric" value={nform.veto_penalty} onChange={setN('veto_penalty')} /></div>
          </div>
          <div className={s.field}>
            <label>Veto</label>
            <div className={s.switchrow}>
              <b>{editing.veto_enabled ? 'Veto on' : 'Veto off'}</b>
              <button className={`${s.sw}${editing.veto_enabled ? '' : ' ' + s.off}`} onClick={() => set({ veto_enabled: !editing.veto_enabled })} aria-label="Toggle veto" />
            </div>
          </div>
          <div className={s.sumline}>Members see: <b>{summarise(preview)}</b></div>
          <div className={s.edfoot}>
            <button className={s.cancel} onClick={() => { setEditing(null); setErr(null); }}>Cancel</button>
            <button className={s.save} onClick={save} disabled={!editing.name.trim() || busy != null}>{busy === 'save' ? 'Saving…' : 'Save preset'}</button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className={s.appbar}>
        <span className={s.ctx}>Scoring presets</span>
        <button className={s.menu} aria-label="Back" onClick={() => nav?.go('admin')}>⋯</button>
      </div>
      <p className={s.intro}>Named rulesets members choose from when they propose a round.</p>
      {err ? <div className={s.banner}>{err}</div> : null}
      {list.loading ? (
        <div className={s.intro}>Loading…</div>
      ) : (
        <div className={s.list}>
          {(list.data ?? []).map((p) => (
            <div key={p.ruleset_id} className={s.card}>
              <div className={s.top}><b>{p.name}</b></div>
              <small>{summarise(p)}</small>
              <div className={s.actions}>
                <button onClick={() => openEditor(p)}>Edit</button>
                <button className={s.del} disabled={busy != null} onClick={() => del(p.ruleset_id)}>{busy === p.ruleset_id ? '…' : 'Delete'}</button>
              </div>
            </div>
          ))}
          {(list.data?.length ?? 0) === 0 ? <p className={s.intro}>No presets yet — add one so members can propose rounds.</p> : null}
        </div>
      )}
      <button className={s.new} onClick={() => openEditor(blank)}>+ New preset</button>
    </Shell>
  );
}
